import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext, HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getErrorMessages, isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    ProductModel,
    ProductSettingsModel,
    PromotionModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getCommaSeparatedDisplayPrice } from "@ucs/Helpers/Formatting";
import ProductAvailability from "@ucs/Overrides/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@ucs/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@ucs/Overrides/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@ucs/Overrides/Components/ProductPrice";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: InjectableCss;
    productLeftSection?: InjectableCss;
    productRightSection?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescriptionSection?: InjectableCss;
    priceSection?: InjectableCss;
    buttonSection?: InjectableCss;
    productAvailabilityLabel?: InjectableCss;
    subtotalLabel?: TypographyProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    configurationOptionText?: TypographyProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantity?: CartLineQuantityStyles;
    qtySample?: InjectableCss;
    qtySampleLabel?: TypographyProps;
    qtySampleText?: TypographyProps;
    extendedUnitNetPrice?: InjectableCss;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageText?: TypographyProps;
    productPrice?: ProductPriceStyles;
    uomSelectBoxWrapper?: InjectableCss;
    uomLabelText?: TypographyPresentationProps;
    uomSelectBox?: SelectPresentationProps;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    costCodeStyles?: InjectableCss;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineIcon?: IconPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    buttonLinkStyles?: LinkPresentationProps;
    messageTextArea?: TextAreaProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        css: css`
            width: 100%;
            background-color: ${UCSColors.gray10};
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            padding: 10px 10px 20px;
            margin-bottom: 10px;
            display: grid;
            gap: 15px;
            grid-template-columns: 56% 44%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                    `,
                    css`
                        grid-template-columns: 1fr;
                    `,
                    css`
                        grid-template-columns: 1fr;
                    `,
                ]);
            }}
            @media only screen and  (min-width: 1200px) and (max-width: 1350px) {
                grid-template-columns: 50% 50%;
            }
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: fit-content;
                box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);
            `,
        },
        img: {
            css: css`
                height: 120px;
                width: 120px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            height: 80px;
                            width: 80px;
                        `,
                        css`
                            height: 80px;
                            width: 80px;
                        `,
                        css`
                            height: 100px;
                            width: 100px;
                        `,
                        css`
                            height: 100px;
                            width: 100px;
                        `,
                    ]);
                }}
            `,
        },
    },
    productLeftSection: {
        css: css`
            display: flex;
            width: 100%;
            align-items: flex-start;
        `,
    },
    productRightSection: {
        css: css`
            display: flex;
            width: calc(100% - 20px);
            flex-direction: column;
            justify-content: space-between;
            gap: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        gap: 24px;
                    `,
                    css`
                        width: 100%;
                        gap: 24px;
                    `,
                    css`
                        width: 100%;
                        gap: 24px;
                    `,
                ]);
            }}
        `,
    },
    productDescriptionSection: {
        css: css`
            display: flex;
            flex-direction: column;
            font-size: 14px;
            line-height: 18px;
            margin-left: 10px;
        `,
    },
    priceSection: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            font-size: 14px;
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        gap: 20px 15px;
                    `,
                    css`
                        gap: 15px;
                    `,
                    css`
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
    buttonSection: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            line-height: 18px;
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                ]);
            }}
        `,
    },
    productAvailabilityLabel: {
        css: css`
            margin-top: 8px;
            color: ${getColor("primary")};
        `,
    },
    subtotalLabel: {
        size: 13,
        weight: 700,
        color: UCSColors.gray04,
        transform: "uppercase",
        lineHeight: "16px",
        css: css`
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
            @media print {
                font-size: 12px;
            }
        `,
    },
    productBrand: {
        nameLink: {
            typographyProps: {
                transform: "uppercase",
                css: css`
                    font-size: 13px;
                    color: ${UCSColors.gray04} !important;
                    line-height: 16px;
                    margin-bottom: 7px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 12px;
                                margin-bottom: 2px;
                            `,
                            css`
                                font-size: 12px;
                                margin-bottom: 2px;
                            `,
                        ]);
                    }}
                `,
            },
        },
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                size: 18,
                color: UCSColors.gray00,
                weight: 500,
                lineHeight: "20px",
                css: css`
                    margin-bottom: 8px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 16px;
                                line-height: 18px;
                            `,
                            css`
                                font-size: 16px;
                                line-height: 18px;
                            `,
                        ]);
                    }}
                `,
            },
        },
    },
    savingsMessageText: {
        size: 14,
        lineHeight: "18px",
        css: css`
            text-align: end;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        text-align: start;
                    `,
                    css`
                        text-align: start;
                    `,
                    css`
                        text-align: start;
                    `,
                ]);
            }}
        `,
    },
    productPartNumbers: {
        container: {
            css: css`
                min-width: 200px;
            `,
        },
        erpNumberGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 13px;
                        `,
                        css`
                            font-size: 13px;
                        `,
                    ]);
                }}
            `,
        },
        erpNumberLabelText: {
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 0;
                min-width: fit-content;
            `,
        },
        erpNumberValueText: {
            css: css`
                min-width: fit-content;
            `,
        },
        customerNameGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
            `,
        },
        customerNameLabelText: {
            variant: "p",
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
            `,
        },
        manufacturerItemGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
            `,
        },
        manufacturerItemLabelText: {
            variant: "p",
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
            `,
        },
    },
    quantity: {
        editableQuantityTextField: {
            labelProps: {
                variant: "p",
                size: 13,
                weight: 700,
                color: UCSColors.gray04,
                lineHeight: "16px",
                css: css`
                    width: fit-content;
                    letter-spacing: 0.1em;
                    margin-bottom: -10px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 12px;
                            `,
                            css`
                                font-size: 12px;
                            `,
                        ]);
                    }}
                    @media print {
                        font-size: 12px;
                    }
                `,
            },
            cssOverrides: {
                formInputWrapper: css`
                    text-align: start;
                    width: 90px;
                `,
                inputSelect: css`
                    height: 30px;
                    text-align: center;
                    padding: 0 5px !important;
                    width: 70px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 17px;
                            `,
                            css`
                                font-size: 17px;
                            `,
                        ]);
                    }}
                    @media print {
                        width: 100px;
                    }
                `,
                formField: css`
                    width: fit-content;
                    color: ${UCSColors.gray03};
                `,
            },
        },
        readOnlyQuantityText: {
            extendedStyles: {
                wrapper: {
                    css: css`
                        color: ${UCSColors.gray03};
                        width: fit-content;
                        text-align: center;
                    `,
                },
            },
        },
    },
    qtySample: {
        css: css`
            width: 90px;
            display: flex;
            flex-direction: column;
            text-align: center;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: fit-content;
                    `,
                    css`
                        width: fit-content;
                    `,
                    css`
                        width: fit-content;
                    `,
                ]);
            }}
        `,
    },
    qtySampleLabel: {
        size: 13,
        weight: 700,
        color: UCSColors.gray04,
        transform: "uppercase",
        lineHeight: "16px",
        css: css`
            letter-spacing: 0.1em;
            width: fit-content;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
            @media print {
                font-size: 12px;
            }
        `,
    },
    qtySampleText: {
        size: 18,
        css: css`
            margin-top: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 17px;
                    `,
                    css`
                        font-size: 17px;
                    `,
                ]);
            }}
        `,
    },
    extendedUnitNetPrice: {
        css: css`
            min-width: 105px;
            display: flex;
            flex-direction: column;
            text-align: end;
            justify-self: flex-end;
            margin-left: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        min-width: 100px;
                    `,
                    css`
                        min-width: 100px;
                    `,
                    css`
                        min-width: 100px;
                    `,
                    css`
                        width: 100%;
                    `,
                ]);
            }}
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
        size: 18,
        color: UCSColors.gray03,
        css: css`
            margin-top: 7px;
            text-align: end;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        size: 18,
        css: css`
            margin-top: 10px;
        `,
    },
    cartLineErrorMessageText: { color: UCSColors.error },
    productPrice: {
        wrapper: {
            css: css`
                width: fit-content;
                span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                    display: inline-block;
                }
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        null,
                        css`
                            width: 100px;
                        `,
                    ]);
                }}
            `,
        },
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 700,
            color: UCSColors.gray04,
            size: 13,
            lineHeight: "16px",
            css: css`
                margin-bottom: 7px;
                letter-spacing: 0.1em;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 12px;
                        `,
                        css`
                            font-size: 12px;
                        `,
                    ]);
                }}
                @media print {
                    font-size: 12px;
                }
            `,
        },
        price: {
            priceText: {
                weight: 400,
                size: 18,
                color: UCSColors.gray03,
                css: css`
                    white-space: nowrap;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 17px;
                            `,
                            css`
                                font-size: 17px;
                            `,
                        ]);
                    }}
                `,
            },
        },
    },
    uomLabelText: {
        transform: "uppercase",
        weight: 700,
        color: UCSColors.gray04,
        size: 13,
        lineHeight: "16px",
        css: css`
            margin-bottom: 6px;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
            @media print {
                font-size: 12px;
            }
        `,
    },
    uomSelectBoxWrapper: {
        css: css`
            width: 60px;
            display: flex;
            flex-direction: column;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 70px !important;
                    `,
                    css`
                        width: 70px !important;
                    `,
                ]);
            }}
            select[data-test-selector="cartLine_unitOfMeasureSelect"] {
                height: 30px;
                font-size: 12px;
            }
            span[class^="IconWrapper"] {
                height: 26px;
                padding: 0;
                width: 16px;
            }
        `,
    },
    promotionNameText: {
        css: css`
            line-height: 18px;
            margin-top: -10px;
            align-self: flex-end;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        align-self: flex-start;
                    `,
                    css`
                        align-self: flex-start;
                    `,
                    css`
                        align-self: flex-start;
                    `,
                ]);
            }}
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeStyles: {
        css: css`
            width: 100%;
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotes: {
        toggleLink: {
            color: "primary",
            typographyProps: { size: 14 },
        },
    },
    removeCartLineIcon: { src: XCircle },
    buttonLinkStyles: {
        color: "primary",
        typographyProps: { size: 14 },
        css: css`
            white-space: nowrap;
        `,
    },
    messageTextArea: {
        css: css`
            resize: vertical;
        `,
        cssOverrides: {
            formInputWrapper: css`
                div {
                    span {
                        color: #000000;
                    }
                    span[data-test-selector$="-error"] {
                        color: ${getColor("danger")};
                    }
                }
            `,
        },
    },
    binInformationsGridItem: {
        width: 12,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    ...props
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);
    const [notesError, setNotesError] = useState("");
    const notesErrorMsg = siteMessage("Order_Notes_Error");
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const [showLineNotesArea, setShowLineNotesArea] = useState(false);
    const [notes, setNotes] = useState(cartLine.notes);

    const lineNotesClickHandler = () => {
        setShowLineNotesArea(!showLineNotesArea);
    };

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const inputValue = event.target.value.length > 800 ? event.target.value.slice(0, 800) : event.target.value;
        if (inputValue === "") {
            setNotesError("");
            setNotes(inputValue);
        } else if (/^\s*$/.test(inputValue)) {
            setNotesError(notesErrorMsg as string);
        } else {
            setNotesError("");
            setNotes(inputValue);
        }
    };

    const blurHandler = () => {
        if (notesError === "") {
            notesChangeHandler(notes);
        }
    };

    const isProductSample = cartLine.erpNumber.toLowerCase().substring(cartLine.erpNumber.length - 1) === "s";
    const heading = showLineNotesArea ? "Hide notes" : cartLine.notes.length === 0 ? "Add notes" : "Show notes";

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const cartUnitOfMeasures = cartLine.properties.unitOfMeasures ? cartLine.properties.unitOfMeasures.split(",") : [];

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedUom = event.currentTarget.value;
        updateCartLine({
            cartLine: {
                ...cartLine,
                unitOfMeasure: selectedUom,
                unitOfMeasureDescription: "",
                unitOfMeasureDisplay: "",
            },
        });
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const errorMessages = getErrorMessages(cart, cartLine, showInventoryAvailability);

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }
    const billtoDefaultWarehouse = cart.properties["customerDefaultWarehouse"];
    const displayInventryProp = cart.properties["displayInventory"];
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    return (
        <StyledWrapper
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.productLeftSection}>
                {showImage && (
                    <ProductImage
                        product={cartLine}
                        extendedStyles={styles.productImage}
                        isProductSample={isProductSample}
                    />
                )}

                <StyledWrapper {...styles.productDescriptionSection}>
                    {showBrand && cartLine.brand && (
                        <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                    )}
                    {showProductTitle && (
                        <ProductDescription
                            product={cartLine}
                            extendedStyles={styles.productDescription}
                            isProductSample={isProductSample}
                        />
                    )}

                    {isProductSample ? (
                        <Typography>{translate("Sample")}</Typography>
                    ) : (
                        <Typography>{cartLine.altText}</Typography>
                    )}

                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        showProductNumber={showProductNumber}
                    />

                    {showVmiBinDetails && currentMode === "Vmi" && (
                        <GridItem {...styles.binInformationsGridItem}>
                            <ProductBinNumbers
                                locationId={cart.vmiLocationId}
                                binId={cartLine.vmiBinId}
                                isCondensed={false}
                                extendedStyles={styles.productBinNumbers}
                            />
                        </GridItem>
                    )}
                    {/* TODO: style when available */}
                    {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                        <>
                            {cartLine.sectionOptions!.map(option => (
                                <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                    {`${option.sectionName}:${option.optionName}`}
                                </Typography>
                            ))}
                        </>
                    )}

                    {/* Display zero-price checkout restriction message */}
                    {cartLine.pricing?.actualPrice === 0 &&
                        cartLine.properties?.allowZeroPriceCheckoutForWarehouse === "false" && (
                            <Typography
                                {...styles.cartLineErrorMessageText}
                                data-test-selector="cartline_zeroPriceRestriction"
                            >
                                {siteMessage("Cart_ZeroPriceNotAllowedForWarehouse")}
                            </Typography>
                        )}

                    {/* TODO: style when available */}
                    {errorMessages.length > 0 && (
                        <>
                            {errorMessages.map((message, index) => (
                                <Typography
                                    {...styles.cartLineErrorMessageText}
                                    key={index}
                                    data-test-selector="cartline_errorMessage"
                                >
                                    {message}
                                </Typography>
                            ))}
                        </>
                    )}

                    {!isProductSample &&
                        showInventoryAvailability &&
                        !cartLine.quoteRequired &&
                        cartLine.availability?.message && (
                            <Typography {...styles.productAvailabilityLabel}>
                                {cartLine.availability.message}
                            </Typography>
                        )}

                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                        customerDefaultWarehouse={billtoDefaultWarehouse}
                        displayInventory={displayInventryProp}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.productRightSection}>
                <StyledWrapper {...styles.priceSection}>
                    {!cart.cartNotPriced && (
                        <ProductPrice
                            product={cartLine}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            showInvalidPriceMessage={true}
                            extendedStyles={styles.productPrice}
                            showLabel={showPriceLabel}
                            showUnitOfMeasure={showPerUnitPrice}
                        />
                    )}
                    <CartLineQuantity
                        cart={cart}
                        editable={true}
                        onQtyOrderedChange={qtyOrderedChangeHandler}
                        extendedStyles={styles.quantity}
                    />
                    {cartLine.properties.unitOfMeasures && cartUnitOfMeasures.length > 1 && (
                        <StyledWrapper {...styles.uomSelectBoxWrapper}>
                            <Typography as="label" {...styles.uomLabelText}>
                                {translate("UOM")}
                            </Typography>
                            <Select
                                value={cartLine.unitOfMeasure}
                                onChange={uomChangeHandler}
                                data-test-selector="cartLine_unitOfMeasureSelect"
                            >
                                {cartUnitOfMeasures.map(uom => (
                                    <option key={uom} value={uom.trim()}>
                                        {uom.trim()}
                                    </option>
                                ))}
                            </Select>
                        </StyledWrapper>
                    )}

                    {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing && (
                        <StyledWrapper {...styles.extendedUnitNetPrice}>
                            <Typography {...styles.subtotalLabel}>{translate("Subtotal")}</Typography>
                            <Typography
                                {...styles.extendedUnitNetPriceText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                            >
                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `$${
                                          cartLine.pricing.extendedUnitRegularPriceWithVat === 0
                                              ? "—"
                                              : getCommaSeparatedDisplayPrice(
                                                    cartLine.pricing.extendedUnitRegularPriceWithVat,
                                                )
                                      }`
                                    : `$${
                                          cartLine.pricing.extendedUnitNetPrice === 0
                                              ? "—"
                                              : getCommaSeparatedDisplayPrice(cartLine.pricing.extendedUnitNetPrice)
                                      }`}
                            </Typography>
                            {enableVat && (
                                <>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {vatPriceDisplay === "DisplayWithVat" ||
                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                            ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                            : translate("Ex. VAT")}
                                    </Typography>
                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <>
                                            <Typography {...styles.subtotalWithoutVatText}>
                                                {`$${
                                                    cartLine.pricing.extendedUnitNetPrice === 0
                                                        ? "—"
                                                        : getCommaSeparatedDisplayPrice(
                                                              cartLine.pricing.extendedUnitNetPrice,
                                                          )
                                                }`}
                                                `
                                            </Typography>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {translate("Ex. VAT")}
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>

                {/* {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && (
                    <ProductPriceSavingsMessage
                        pricing={cartLine.pricing}
                        qtyOrdered={cartLine.qtyOrdered || 1}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        currencySymbol="$"
                        {...styles.savingsMessageText}
                    />
                )} */}

                {/* {showPricing &&
                        promotions.map(promotion => (
                            <Typography {...styles.promotionNameText} key={promotion.id}>
                                {promotion.name}
                            </Typography>
                    ))} */}

                {/* TODO: style when available */}
                {cart.showCostCode && !cartLine.isPromotionItem && (
                    <StyledWrapper {...styles.costCodeStyles}>
                        <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                        {cart.canEditCostCode ? (
                            <Select
                                value={cartLine.costCode}
                                onChange={costCodeChangeHandler}
                                aria-label={translate("Cost Code")}
                                {...styles.costCodeSelect}
                            >
                                {cart.costCodes?.map(costCode => (
                                    <option key={costCode.costCode} value={costCode.costCode}>
                                        {costCode.description}
                                    </option>
                                ))}
                            </Select>
                        ) : (
                            <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                        )}
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.buttonSection}>
                    {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                        <Link
                            {...styles.buttonLinkStyles}
                            onClick={lineNotesClickHandler}
                            data-test-selector="cartline_notesShowHideButton"
                        >
                            {translate(heading)}
                        </Link>
                    )}
                    {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                        <Link {...styles.buttonLinkStyles} onClick={addToListClickHandler}>
                            {translate("Add to List")}
                        </Link>
                    )}
                    {showRemoveAction && (
                        <Link
                            {...styles.buttonLinkStyles}
                            onClick={removeCartLineClickHandler}
                            data-test-selector="cartline_removeLine"
                        >
                            {translate("Remove item")}
                        </Link>
                    )}
                </StyledWrapper>
                {showLineNotesArea && (
                    <TextArea
                        {...styles.messageTextArea}
                        value={notes}
                        disabled={!cart.canModifyOrder}
                        onChange={changeHandler}
                        onBlur={blurHandler}
                        hint={`${800 - notes.length} ${translate("characters left")}`}
                        data-test-selector="cartline_notes"
                        error={notesError}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
